import React, { FC, useEffect, useState } from "react"
import Loading from "../../../ui/Loading"
import { KTitleSpan, KButton, KSpan } from "kahuna-base-react-components"
import { handleAuthorizedRequest, notifications as notificationsApi } from "../../../../apis"
import { UserNotification } from "../../../../types"
import { mapListToCamelCase } from "../../../../utility"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { SetURLSearchParams } from "react-router-dom"
import SearchUserComponent from "../../../ui/components/SearchUserComponent"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import Pagination from "../../../ui/table/Pagination"

const columnWidthsWithUser: any = {
  ID: "5%",
  USER_ID: "7%",
  CREATED: "12%",
  CONTENT: "25%",
  READ: "8%",
  READ_ON: "10%",
  READ_AT: "12%",
  TYPE: "8%",
  URL: "13%"
}

const columnWidthsDefault: any = {
  ID: "5%",
  CREATED: "12%",
  CONTENT: "30%",
  READ: "8%",
  READ_ON: "10%",
  READ_AT: "12%",
  TYPE: "8%",
  URL: "15%"
}

type AdminNotificationWatchProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
}

const AdminNotificationWatch: FC<AdminNotificationWatchProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>(undefined)
  const [offset, setOffset] = useState(0)
  const [limit] = useState(20)
  const [notificationList, setNotificationList] = useState<UserNotification[]>([])
  const [totalCount, setTotalCount] = useState(0)

  const fetchNotifications = (currentOffset: number) => {
    setLoading(true)
    const userParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    handleAuthorizedRequest(
      async () => {
        const response = await notificationsApi(
          `/admin_user_notifications/?limit=${limit}&offset=${currentOffset}${userParam}`,
          "get"
        )
        if (response.status === 200) {
          const mapped = mapListToCamelCase(response.data.results)
          setNotificationList(mapped)
          setTotalCount(response.data.count || 0)
        }
        setLoading(false)
      },
      "Error fetching notifications",
      null
    )
  }

  useEffect(() => {
    setOffset(0)
    fetchNotifications(0)
  }, [selectedUser])

  const getReadOnLabel = (readOn: string | undefined) => {
    if (!readOn) return "-"
    return readOn === "mobile" ? "📱 Mobile" : "🌐 Web"
  }

  const renderNotifications = () => {
    if (notificationList.length === 0 && !loading) {
      return (
        <div className="flex justify-center items-center py-20">
          <KSpan text={selectedUser ? "No notifications found for this user" : "No notifications found"} color="#999" />
        </div>
      )
    }

    const columnWidths = selectedUser ? columnWidthsDefault : columnWidthsWithUser

    return (
      <div className="pt-4 pb-4 flex flex-col">
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => (
            <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
              <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
            </div>
          ))}
        </div>
        {notificationList.map((notification) => (
          <div
            key={`notif_${notification.id}`}
            style={{ width: "100%" }}
            className="pt-5"
          >
            <div className="flex pt-2 gap-2" style={{ width: "100%", borderTop: "1px dashed grey" }}>
              <div style={{ width: columnWidths.ID }} className="flex items-center">
                <KSpan text={notification.id?.toString() || "-"} />
              </div>
              {!selectedUser && (
                <div style={{ width: columnWidths.USER_ID }} className="flex items-center">
                  <KSpan text={notification.userId?.toString() || "-"} />
                </div>
              )}
              <div style={{ width: columnWidths.CREATED }} className="flex items-center">
                <KSpan text={notification.created?.substring(0, 19)?.replace("T", " ") || "-"} />
              </div>
              <div style={{ width: columnWidths.CONTENT }} className="flex items-center">
                <KSpan text={notification.param1 || "-"} />
              </div>
              <div style={{ width: columnWidths.READ }} className="flex items-center">
                <div
                  className="px-2 py-1 rounded-md"
                  style={{
                    background: notification.read ? "#E8F5E9" : "#FFF3E0",
                    border: `1px solid ${notification.read ? "#A5D6A7" : "#FFB74D"}`
                  }}
                >
                  <KSpan
                    text={notification.read ? "Read" : "Unread"}
                    fontSize={12}
                    color={notification.read ? "#2E7D32" : "#E65100"}
                  />
                </div>
              </div>
              <div style={{ width: columnWidths.READ_ON }} className="flex items-center">
                <KSpan text={getReadOnLabel(notification.readOn)} />
              </div>
              <div style={{ width: columnWidths.READ_AT }} className="flex items-center">
                <KSpan text={notification.readAt?.substring(0, 19)?.replace("T", " ") || "-"} />
              </div>
              <div style={{ width: columnWidths.TYPE }} className="flex items-center">
                <KSpan text={notification.notificationId === 2 ? "Release" : "Other"} />
              </div>
              <div style={{ width: columnWidths.URL }} className="flex items-center overflow-hidden">
                <KSpan text={notification.urlParam || notification.relatedUrl || "-"} fontSize={12} />
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTitleSpan fontSize={20} lineHeight="28px" text="Notification Watch" />
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <KSpan text="User" />
            <SearchUserComponent className="relative" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          </div>
          <div>
            <KButton
              height="35px"
              text="Apply"
              background="black"
              textColor="white"
              onClick={() => {
                setOffset(0)
                fetchNotifications(0)
              }}
            />
          </div>
        </div>
      </div>
      {renderNotifications()}
      <Pagination
        hidden={totalCount <= limit}
        pageCount={Math.ceil(totalCount / limit)}
        currentPage={Math.floor(offset / limit) + 1}
        onClickPage={(page: number) => {
          const newOffset = (page - 1) * limit
          setOffset(newOffset)
          fetchNotifications(newOffset)
        }}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminNotificationWatch)
